import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  loadBlogList,
  loadBlogSearchIndex,
  loadStandalonePage,
} from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";
import { Pagination } from "~/components/Pagination";
import { BlogCard, type BlogCardData } from "~/components/BlogCard";
import { BlogNewsletterSidebar } from "~/components/BlogNewsletterSidebar";

export const Route = createFileRoute("/blog/")({
  loader: async () => {
    const [listData, sectionData, searchIndex] = await Promise.all([
      loadBlogList({ data: { page: 1 } }),
      loadStandalonePage({ data: { path: "blog/_index.md" } }).catch(
        () => null,
      ),
      loadBlogSearchIndex(),
    ]);
    return { ...listData, section: sectionData, searchIndex };
  },
  head: () => ({
    meta: seo({
      title: "Blog",
      description:
        "Insights on identity security, access governance, and modern IGA from the C1 team.",
      url: "/blog",
    }),
    links: seoLinks({ url: "/blog" }),
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const { posts, page, totalPages, section, featuredPost, searchIndex } =
    Route.useLoaderData();
  const [query, setQuery] = useState("");

  // Scored search over the full blog index. When the query is blank we
  // render the paginated view unchanged. When it has content we keep
  // the page header and featured post visible but replace the grid
  // below with scored results across every blog post.
  const searchResults = useMemo(() => {
    const q = query.trim();
    if (!q) return null;
    return searchBlogPosts(searchIndex.posts, q);
  }, [query, searchIndex.posts]);

  const isSearching = searchResults !== null;

  return (
    <>
      {/* ============ Page 1 Header ============ */}
      {page === 1 && (
        <div className="mx-auto max-w-screen-xl px-4 py-8 md:px-8 md:py-8">
          <div className="my-4 flex flex-col justify-between">
            <div>
              <div className="my-4">
                <h1 className="chip bg-pink-accent text-charcoal">{section?.title || "Blog"}</h1>
              </div>
            </div>
            <div className="my-4 font-display text-xl font-bold leading-tight text-charcoal md:w-1/2 md:text-4xl">
              <p>
                {section?.tagline ||
                  "Insights on identity security, access governance, and modern IGA."}
              </p>
            </div>
            <a
              href="/blog/feed.xml"
              className="inline-flex items-center gap-1.5 font-body text-sm font-medium text-teal-dark transition-colors hover:text-teal-dark/80"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.18 15.64a2.18 2.18 0 1 1 0 4.36 2.18 2.18 0 0 1 0-4.36zM4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44zm0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93v-2.83z" />
              </svg>
              RSS Feed
            </a>
          </div>

          {/* Featured post + Newsletter sidebar */}
          <div className="flex flex-col space-y-6 md:flex-row md:space-x-6 md:space-y-0">
            {/* Featured post card (2/3 width) */}
            {featuredPost && (
              <div className="relative md:w-2/3">
                {featuredPost.featured_image && (
                  <Link
                    to="/blog/$slug"
                    params={{ slug: featuredPost.slug }}
                    className="block overflow-hidden rounded"
                  >
                    <img
                      src={featuredPost.featured_image}
                      alt={featuredPost.title}
                      className="w-full"
                    />
                  </Link>
                )}
                <div className="my-4 rounded bg-white pb-0 md:pointer-events-none md:absolute md:-bottom-10 md:w-3/5 md:rounded-tl-none md:p-6">
                  <h2 className="font-body text-2xl font-bold leading-tight text-charcoal">
                    <Link
                      to="/blog/$slug"
                      params={{ slug: featuredPost.slug }}
                    >
                      {featuredPost.title}
                    </Link>
                  </h2>
                  <div className="mt-1 flex space-x-2 font-body text-sm text-gray-60 md:mt-2">
                    <time>
                      {new Date(featuredPost.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </time>
                    {featuredPost.authors.length > 0 && (
                      <>
                        <span>&middot;</span>
                        <span>{featuredPost.authors[0].name}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Newsletter form (1/3 width) */}
            <div className="relative flex flex-col overflow-hidden rounded-20 border border-[#DCDADA] bg-teal-dark md:w-1/3">
              <div className="p-4 pb-0 lg:p-6 lg:pb-3">
                <p
                  className="font-display text-2xl font-bold leading-tight text-white lg:text-3xl"
                  dangerouslySetInnerHTML={{
                    __html:
                      section?.form_heading || "Stay in touch",
                  }}
                />
                <p
                  className="mt-2 font-body leading-snug text-white/70 lg:mt-3 lg:text-lg lg:leading-snug"
                  dangerouslySetInnerHTML={{
                    __html:
                      section?.form_copy ||
                      "The best way to keep up with identity security tips, guides, and industry best practices.",
                  }}
                />
                <BlogNewsletterInlineForm />
              </div>
              {/* Decorative stacked cards SVG */}
              <svg
                className="w-full"
                width="378"
                height="115"
                viewBox="0 0 378 115"
                fill="none"
              >
                <path
                  d="M43 35.475C43 33.973 44.218 32.755 45.72 32.755H332.28C333.782 32.755 335 33.973 335 35.475V164.246H43V35.475Z"
                  fill="#8AF1D9"
                  fillOpacity="0.15"
                />
                <path
                  d="M57 52.72C57 51.218 58.218 50 59.72 50H319.28C320.782 50 322 51.218 322 52.72V64.28C322 65.782 320.782 67 319.28 67H59.72C58.218 67 57 65.782 57 64.28V52.72Z"
                  fill="#8AF1D9"
                  fillOpacity="0.3"
                />
                <path
                  d="M57 79.72C57 78.218 58.218 77 59.72 77H319.28C320.782 77 322 78.218 322 79.72V145H57V79.72Z"
                  fill="#8AF1D9"
                  fillOpacity="0.25"
                />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* ============ Search + Post grid ============ */}
      <div className="pb-10 lg:pb-16">
        <div className="mx-auto max-w-screen-xl px-4 py-2 md:px-8 md:py-8">
          {/* Search box */}
          <div className="mb-6 md:mb-8">
            <label className="block">
              <span className="sr-only">Search the blog</span>
              <div className="relative">
                <svg
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-60"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <circle cx="9" cy="9" r="6" />
                  <path d="m14 14 4 4" strokeLinecap="round" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search blog posts by topic or title"
                  aria-label="Search blog posts"
                  // `blog-search-input` hides the native WebKit/Chrome
                  // "x" decoration on type=search so only our custom
                  // clear button is shown. Styles live in app.css.
                  className="blog-search-input w-full rounded border border-[#DCDADA] bg-white py-3 pl-11 pr-10 font-body text-base text-charcoal placeholder:text-gray-60 focus:border-teal-dark focus:outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-gray-60 hover:text-charcoal"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M5 5l10 10M15 5 5 15" />
                    </svg>
                  </button>
                )}
              </div>
            </label>
            {isSearching && (
              <p className="mt-3 font-body text-sm text-gray-60">
                {searchResults!.length === 0
                  ? `No results for "${query.trim()}".`
                  : `${searchResults!.length} result${searchResults!.length === 1 ? "" : "s"} for "${query.trim()}"`}
              </p>
            )}
          </div>

          {isSearching ? (
            searchResults!.length > 0 && (
              <ul className="grid gap-6 md:grid-cols-3">
                {searchResults!.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </ul>
            )
          ) : (
            <>
              <ul className="grid gap-6 md:grid-cols-3">
                {posts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </ul>

              <Pagination
                currentPage={page}
                totalPages={totalPages}
                basePath="/blog"
              />
            </>
          )}
        </div>
      </div>

      {/* ============ Bottom newsletter section ============ */}
      <div className="pb-14 md:pb-24">
        <div className="mx-auto max-w-screen-xl px-4 md:px-8">
          <BlogNewsletterSidebar />
        </div>
      </div>
    </>
  );
}

/**
 * Inline newsletter form for the blog list header sidebar.
 * Simpler than BlogNewsletterSidebar — no card wrapper, just the form.
 */
function BlogNewsletterInlineForm() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(
        "https://api.hsforms.com/submissions/v3/integration/submit/9161697/a080248a-fad5-4e84-a0ed-e74f5e8496ec",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: [{ name: "email", value: email }],
            context: {
              pageUri: typeof window !== "undefined" ? window.location.href : "",
              pageName: typeof document !== "undefined" ? document.title : "",
            },
          }),
        },
      );
      if (res.ok) setSubmitted(true);
    } catch {
      /* swallow */
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <p className="mt-5 font-body text-sm font-medium text-white">
        Thanks! We&apos;ll be in touch.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-3 lg:mt-5">
      <label className="w-full">
        <span className="sr-only">Email</span>
        <input
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          required
          className="w-full rounded-10 border border-white/20 bg-white/10 px-4 py-2.5 font-body text-base text-white placeholder:text-white/50 focus:border-white/40 focus:outline-none"
        />
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="inline-block w-full rounded-10 bg-cream py-2.5 font-body font-medium text-dark hover:bg-white disabled:opacity-50"
      >
        {submitting ? "..." : "Submit"}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------
// Search scoring
// ---------------------------------------------------------------------

interface SearchablePost {
  slug: string;
  title: string;
  description: string;
  date: string;
  featured_image: string;
  authors: BlogCardData["authors"];
  topics: string[];
}

/**
 * Common English stopwords + a few domain-specific filler terms. These
 * are ignored when deciding whether a post matches a query, so a search
 * for "the guide to access reviews" still requires "access" and
 * "reviews" to be present but doesn't penalize a post for lacking "the".
 */
const STOPWORDS = new Set([
  "a", "an", "the", "of", "for", "to", "and", "or", "is", "are", "am",
  "in", "on", "with", "by", "at", "from", "as", "be", "but", "if", "it",
  "this", "that", "these", "those", "what", "how", "why", "when", "who",
  "your", "our", "my",
]);

/**
 * Escape a string for use inside a RegExp character class or pattern.
 */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build a word-prefix matcher for a token.
 *
 * Anchors on a leading word boundary (start-of-string or a
 * non-alphanumeric character) but does NOT require a trailing word
 * boundary. This has two important effects:
 *
 *   - "ai" still won't match inside "gain" or "maintain" because
 *     those words don't start with "ai".
 *   - A partially-typed token like "managemen" matches "management"
 *     while the user is still typing, so results appear incrementally
 *     instead of disappearing on the last keystroke before completion.
 */
function wordMatcher(token: string): RegExp {
  return new RegExp(`(?:^|[^a-z0-9])${escapeRegex(token)}`, "i");
}

/**
 * Score a post against a query. Returns 0 when the post should be
 * excluded from results.
 *
 * Policy (intentionally strict to avoid 100+ result blowouts on common
 * terms like "access" or "management"):
 *
 *   1. Tokenize the query, drop stopwords, keep tokens with >=2 chars.
 *   2. A post only matches if **every** meaningful token appears as a
 *      whole word in its searchable text (title + description + topics).
 *      Whole-word matching is critical: without it, "ai" would match
 *      "gain", "again", "maintain" and flood results.
 *   3. Score boosts favor phrase matches over scattered token matches,
 *      and title/topic hits over description hits:
 *        - Full phrase in title       +100
 *        - Full phrase in a topic      +80
 *        - Full phrase in description  +40
 *        - Each token in title         +10
 *        - Each token in a topic        +8
 *        - Each token in description    +2
 */
function scorePost(post: SearchablePost, query: string): number {
  const phrase = query.toLowerCase().trim();
  if (!phrase) return 0;

  const title = post.title.toLowerCase();
  const description = (post.description || "").toLowerCase();
  const topicsJoined = post.topics.map((t) => t.toLowerCase()).join(" | ");
  const haystack = `${title} ${topicsJoined} ${description}`;

  const rawTokens = phrase.split(/\s+/).filter((t) => t.length >= 2);
  const tokens = rawTokens.filter((t) => !STOPWORDS.has(t));

  // If after stripping stopwords nothing is left (e.g. query "the a of"),
  // fall back to the raw tokens so the user still sees something.
  const required = tokens.length > 0 ? tokens : rawTokens;
  if (required.length === 0) return 0;

  // Hard gate: every required token must appear as a whole word in the
  // searchable text. This is what kills the "119 results" problem.
  for (const tok of required) {
    if (!wordMatcher(tok).test(haystack)) return 0;
  }

  let score = 0;

  // Phrase matches are substring-based on purpose: "ai access" should
  // still match "AI access management" even though the phrase lands
  // mid-title. The gate above already rejected noise.
  if (title.includes(phrase)) score += 100;
  if (topicsJoined.includes(phrase)) score += 80;
  if (description.includes(phrase)) score += 40;

  for (const tok of required) {
    const re = wordMatcher(tok);
    if (re.test(title)) score += 10;
    if (re.test(topicsJoined)) score += 8;
    if (re.test(description)) score += 2;
  }

  return score;
}

function searchBlogPosts<T extends SearchablePost>(posts: T[], query: string): T[] {
  const scored: Array<{ post: T; score: number }> = [];
  for (const post of posts) {
    const score = scorePost(post, query);
    if (score > 0) scored.push({ post, score });
  }
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    // Tie-breaker: newer posts first.
    return new Date(b.post.date).getTime() - new Date(a.post.date).getTime();
  });
  return scored.map((s) => s.post);
}
